/* eslint-disable @typescript-eslint/default-param-last */
import {
  CANDIDATE_JOINED,
  CREATE_OR_UPDATE_CODING_SOLUTION,
  END_INTERVIEW_FOR_ALL,
  GET_CODING_SOLUTION,
  GET_WHITEBOARD_SOLUTION,
  LEAVE_INTERVIEW,
  MERGE_AND_UPDATE_CODE,
  MERGE_AND_UPDATE_DRAWING,
  MOVE_CANDIDATE_TO_LOBBY,
  REMOVE_QUESTION,
  REMOVE_SOLUTION,
  SAVE_ELEMENTS_AND_FILES,
  SET_IS_GET_SOLUTION,
  SET_IS_GET_WHITEBOARD_SOLUTION,
  UPDATE_CODE,
  UPDATE_TECHNOLOGY,
  UPDATE_TEST_CASE_RESULT_IN_SOLUTION,
  SET_SUBJECTIVE_SOLUTION,
  SET_SUBJECTIVE_SOLUTION_MODIFIED_AT,
  SET_SUBJECTIVE_ATTACHMENTS,
  CREATE_OR_UPDATE_SUBJECTIVE_SOLUTION,
  GET_SUBJECTIVE_SOLUTION,
} from "@/constants/ActionTypes";
import initialState from "@/store/initialState";
import { IGetServerTime } from "@/types/serverTime.types";

import {
  IAttachment,
  ISolutionType,
  ISubjectiveSolution,
} from "@/types/solutions.types";
import { getLocalStorageItem } from "@/utils/localStorageHelper";
import { getServerTime } from "@/utils/utils";

const solutionsReducer = (
  state = initialState.solutions,
  action: ISolutionType,
) => {
  switch (action.type) {
    case `${GET_CODING_SOLUTION}_SUCCESS`: {
      const allSolutions = { ...state.allSolutions };
      allSolutions[action.payload.questionSlug] = action.payload.solution;
      return {
        ...state,
        allSolutions,
      };
    }
    case SET_IS_GET_WHITEBOARD_SOLUTION:
    case SET_IS_GET_SOLUTION: {
      const { questionSlug, isSolutionGet } = action.payload;
      return {
        ...state,
        isSolutionsGet: {
          ...state.isSolutionsGet,
          [questionSlug]: isSolutionGet,
        },
      };
    }
    case UPDATE_CODE: {
      const serverTimeOffsetTime =
        getLocalStorageItem<IGetServerTime>("serverTime")?.offsetTime;
      const allSolutions = { ...state.allSolutions };
      const solution = { ...allSolutions[action.payload.questionSlug] };
      if (solution && typeof action.payload.code === "string") {
        solution.code = action.payload.code;
        solution.codeModifiedAt = getServerTime(serverTimeOffsetTime);
      }
      allSolutions[action.payload.questionSlug] = solution;
      return {
        ...state,
        allSolutions,
      };
    }
    case UPDATE_TECHNOLOGY: {
      const serverTimeOffsetTime =
        getLocalStorageItem<IGetServerTime>("serverTime")?.offsetTime;
      const allSolutions = { ...state.allSolutions };
      let solution = { ...allSolutions[action.payload.questionSlug] };
      if (solution) {
        solution.technology = action.payload.technology;
        solution.languageModifiedAt = getServerTime(serverTimeOffsetTime);
      } else {
        solution = {
          attachments: [],
          code: "",
          technology: action.payload.technology,
          id: null,
          languageModifiedAt: getServerTime(serverTimeOffsetTime),
          codeModifiedAt: undefined,
        };
      }
      allSolutions[action.payload.questionSlug] = solution;
      return {
        ...state,
        allSolutions,
      };
    }
    case `${MERGE_AND_UPDATE_CODE}_SUCCESS`: {
      const allSolutions = { ...state.allSolutions };
      let solution = { ...allSolutions[action.payload.questionSlug] };
      if (solution) {
        solution.technology = action.payload.technology;
        solution.languageModifiedAt = action.payload.languageModifiedAt;
        solution.codeModifiedAt = action.payload.codeModifiedAt;
        solution.code = action.payload.code;
      } else {
        solution = {
          attachments: [],
          code: action.payload.code,
          technology: action.payload.technology,
          id: null,
          languageModifiedAt: action.payload.languageModifiedAt,
          codeModifiedAt: action.payload.codeModifiedAt,
        };
      }
      allSolutions[action.payload.questionSlug] = solution;
      return {
        ...state,
        allSolutions,
      };
    }
    case UPDATE_TEST_CASE_RESULT_IN_SOLUTION: {
      const allSolutions = { ...state.allSolutions };
      const { solutionId, status, evaluation } = action.payload;
      const questionSlug = Object.keys(allSolutions).find(
        (currentSolution) => allSolutions[currentSolution].id === solutionId,
      );
      if (questionSlug) {
        const solution = { ...allSolutions[questionSlug] };
        solution.status = status;
        solution.evaluation = evaluation;
        allSolutions[questionSlug] = solution;
      }
      return { ...state, allSolutions };
    }
    case `${CREATE_OR_UPDATE_CODING_SOLUTION}_SUCCESS`: {
      const { questionSlug, solution } = action.payload;
      const allSolutions = { ...state.allSolutions };
      const currentSolution = allSolutions[questionSlug];
      solution.code = currentSolution?.code;
      solution.technology = currentSolution?.technology;
      solution.codeModifiedAt = currentSolution?.codeModifiedAt;
      solution.languageModifiedAt = currentSolution?.languageModifiedAt;
      allSolutions[questionSlug] = solution;
      return {
        ...state,
        allSolutions,
      };
    }
    case `${REMOVE_QUESTION}_SUCCESS`:
    case REMOVE_SOLUTION: {
      const { questionSlug } = action.payload;
      const allSolutions = { ...state.allSolutions };
      delete allSolutions[questionSlug];
      const isSolutionsGet = { ...state.isSolutionsGet };
      isSolutionsGet[action.payload.questionSlug] = false;
      return {
        ...state,
        allSolutions,
        isSolutionsGet,
      };
    }
    case `${MERGE_AND_UPDATE_DRAWING}_SUCCESS`:
    case SAVE_ELEMENTS_AND_FILES:
    case `${GET_WHITEBOARD_SOLUTION}_SUCCESS`: {
      const { elements, files, questionSlug, modifiedAt } = action.payload;
      const currentSolution = state.whiteboardSolutions[questionSlug];
      const updatedFiles = { ...(currentSolution?.files ?? {}), ...files };
      return {
        ...state,
        whiteboardSolutions: {
          ...state.whiteboardSolutions,
          [questionSlug]: {
            elements,
            files: updatedFiles,
            modifiedAt,
          },
        },
      };
    }
    case SET_SUBJECTIVE_SOLUTION: {
      const { questionSlug, solution } = action.payload as unknown as {
        questionSlug: string;
        solution: ISubjectiveSolution;
      };
      return {
        ...state,
        subjectiveSolutions: {
          ...state.subjectiveSolutions,
          [questionSlug]: solution,
        },
      };
    }
    case SET_SUBJECTIVE_ATTACHMENTS: {
      const { questionSlug, attachments } = action.payload as unknown as {
        questionSlug: string;
        attachments: IAttachment[];
      };
      const currentSolution = state.subjectiveSolutions[questionSlug];
      if (!currentSolution) return state;
      return {
        ...state,
        subjectiveSolutions: {
          ...state.subjectiveSolutions,
          [questionSlug]: { ...currentSolution, attachments },
        },
      };
    }
    case SET_SUBJECTIVE_SOLUTION_MODIFIED_AT: {
      const { questionSlug, modifiedAt } = action.payload as unknown as {
        questionSlug: string;
        modifiedAt: Date;
      };
      const currentSolution = state.subjectiveSolutions[questionSlug];
      if (!currentSolution) return state;
      return {
        ...state,
        subjectiveSolutions: {
          ...state.subjectiveSolutions,
          [questionSlug]: { ...currentSolution, modifiedAt },
        },
      };
    }
    case `${CREATE_OR_UPDATE_SUBJECTIVE_SOLUTION}_SUCCESS`: {
      const { questionSlug, slug } = action.payload as unknown as {
        questionSlug: string;
        slug: string;
      };
      const currentSolution = state.subjectiveSolutions[questionSlug];
      if (!currentSolution) return state;
      return {
        ...state,
        subjectiveSolutions: {
          ...state.subjectiveSolutions,
          [questionSlug]: { ...currentSolution, slug },
        },
      };
    }
    case `${GET_SUBJECTIVE_SOLUTION}_SUCCESS`: {
      const { questionSlug, solution } = action.payload as unknown as {
        questionSlug: string;
        solution: ISubjectiveSolution;
      };
      return {
        ...state,
        subjectiveSolutions: {
          ...state.subjectiveSolutions,
          [questionSlug]: solution,
        },
      };
    }
    case CANDIDATE_JOINED:
    case `${LEAVE_INTERVIEW}_SUCCESS`:
    case `${MOVE_CANDIDATE_TO_LOBBY}_SUCCESS`:
    case `${END_INTERVIEW_FOR_ALL}_SUCCESS`:
      return initialState.solutions;
    default:
      return state;
  }
};

export default solutionsReducer;
